import { Allocation_Status, CompliancePageListAllocationComplianceQuery } from '@/__generated__/graphql';
import ActionDropdown from '@/components/basicComponents/actionDropdown';
import Tag from '@/components/basicComponents/tag';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { Loader } from '@/components/loaders/loader';
import NoResults from '@/components/table/noResults';
import RedirectTitleCell from '@/components/table/redirectTitleCell';
import {
  TableBodyWrapper,
  TableFixedCell,
  TableRowCell,
  TableScrollableCellsWrapper,
  TableWithHorizontalScroll,
  TableWithHorizontalScrollRowWrapper,
  TableWithHorizontalScrollWrapper,
  TextCell
} from '@/components/table/styles';
import TableHeader from '@/components/table/tableHeader';
import { ISort, TableColumns } from '@/components/table/types';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { getUTCDate } from '@/shared/utils/date';
import { TenantType } from '@trace-8/trace8-schema';
import { useTranslation } from 'react-i18next';

export const getAllocationHistoryTableDetailsCell = (details: string, status: Allocation_Status) => {
  if (!details) return '';

  const detailsData = {
    [Allocation_Status.Committed]: (
      <>
        Amount: <FormattingTooltip zIndex={1000}>{+details}</FormattingTooltip>
      </>
    ),
    [Allocation_Status.InternalReviewRejected]: `Suitability: ${details}`,
    [Allocation_Status.Terminated]: `Reason: ${details}`
  };

  return detailsData[status as keyof typeof detailsData] || '';
};

interface AllocationHistoryTableProps {
  tableColumns: TableColumns[];
  tableRows: CompliancePageListAllocationComplianceQuery['ListAllocationCompliance']['allocations'];
  sort: ISort;
  loading: boolean;
  setSort: (value: ISort) => void;
  openPage: (path: string) => void;
  viewSignaturePacket: (id: string) => Promise<void>;
}

export const AllocationHistoryTable = ({
  tableColumns,
  tableRows,
  sort,
  loading,
  setSort,
  openPage,
  viewSignaturePacket
}: AllocationHistoryTableProps) => {
  const { t } = useTranslation('enums');
  const { isTablet } = useResponsive();
  const { user } = useAuth();
  const isPlatformManagerUser = user && user?.tenant?.type === TenantType.PLATFORM_MANAGER;

  // 5 is the gap (spacing) between table cells
  const totalTableWidth = tableColumns.reduce((acc, column) => acc + column.width + 5, 0);

  return (
    <TableWithHorizontalScrollWrapper isTablet={isTablet}>
      <TableWithHorizontalScroll totalWidth={`${totalTableWidth}px`}>
        <TableHeader refetch={() => {}} columns={tableColumns} savedSort={sort} savedSetSort={setSort} withHorizontalScroll />
        <TableBodyWrapper>
          {loading ? (
            <Loader />
          ) : (
            <>
              {!tableRows.length ? (
                <NoResults />
              ) : (
                <>
                  {tableRows.map((row) => (
                    <TableWithHorizontalScrollRowWrapper key={row.id}>
                      <TableFixedCell id="fixedCell" width={tableColumns[0].width} alignItems="center">
                        <RedirectTitleCell
                          onClick={() => openPage(`/investors/${row.legalEntity?.family?.id}/${row.legalEntity?.id}?backToTitle=Allocation History`)}
                        >
                          {row.legalEntity.entityName}
                        </RedirectTitleCell>
                      </TableFixedCell>
                      <TableScrollableCellsWrapper alignItems="center">
                        <TableRowCell width={tableColumns[1].width} withHorizontalScroll>
                          <RedirectTitleCell onClick={() => openPage(`/strategyDetails/${row?.investment?.id}?backToTitle=Allocation History`)}>
                            {row?.investment?.name}
                          </RedirectTitleCell>
                        </TableRowCell>
                        <TableRowCell width={tableColumns[2].width} withHorizontalScroll>
                          <TextCell>{row.statusDate && getUTCDate(row.statusDate)}</TextCell>
                        </TableRowCell>
                        <TableRowCell width={tableColumns[3].width} withHorizontalScroll>
                          <TextCell>
                            {row?.status && (
                              <Tag styleType={row?.status === Allocation_Status.Committed ? 'valid' : 'warning'} size="md">
                                {t(`enums:ALLOCATION_STATUS.${row?.status}`) ?? ''}
                              </Tag>
                            )}
                          </TextCell>
                        </TableRowCell>
                        <TableRowCell width={tableColumns[4].width} withHorizontalScroll>
                          <TextCell>{getAllocationHistoryTableDetailsCell(row?.details, row?.status)}</TextCell>
                        </TableRowCell>
                        <TableRowCell width={tableColumns[5].width} withHorizontalScroll>
                          {isPlatformManagerUser && <TextCell>{row.advisoryFirm.name}</TextCell>}
                        </TableRowCell>
                        <TableRowCell width={tableColumns[6].width} withHorizontalScroll>
                          {!!row?.subscriptionDocuments?.length && (
                            <ActionDropdown
                              options={[
                                {
                                  name: 'View Signature Packet',
                                  onClick: () => viewSignaturePacket(row.subscriptionDocuments?.[0]?.id)
                                }
                              ]}
                            />
                          )}
                        </TableRowCell>
                      </TableScrollableCellsWrapper>
                    </TableWithHorizontalScrollRowWrapper>
                  ))}
                </>
              )}
            </>
          )}
        </TableBodyWrapper>
      </TableWithHorizontalScroll>
    </TableWithHorizontalScrollWrapper>
  );
};
